import { useMemo, useState } from "react"
import { useQuery } from "react-query"
import { useParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { InformationCircleIcon, ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/outline"
import { PageView, PageTopBar } from "../../components"
import { Loader } from "../../components/Loader"
import { fetchWorkShifts, fetchWarehouseOperators } from "../../services/workforceServices"
import { WorkforceTabs } from "./WorkforceTabs"
import { ShiftCard } from "./components/ShiftCard"
import { ShiftFormDialog } from "./components/ShiftFormDialog"

export const WorkShiftsContainer = () => {
    const { warehouse_id } = useParams()
    const { t } = useTranslation()

    const [showInactive, setShowInactive] = useState(false)
    const [helpOpen, setHelpOpen] = useState(false)
    const [dialog, setDialog] = useState(null) // null | { shift: null | object }

    const queryKey = ['workforce', warehouse_id, 'shifts', { include_inactive: showInactive }]
    const { data, isLoading, isError, error } = useQuery(
        queryKey,
        () => fetchWorkShifts(warehouse_id, showInactive ? { include_inactive: true } : {}),
        { keepPreviousData: true }
    )

    const shifts = useMemo(() => {
        const raw = Array.isArray(data) ? data : (data?.work_shifts || data?.shifts || [])
        return raw
    }, [data])

    const { data: operatorsData } = useQuery(
        ['workforce', warehouse_id, 'operators', { for_shift_counts: true }],
        () => fetchWarehouseOperators(warehouse_id, {}),
        { refetchOnWindowFocus: false, staleTime: 1000 * 30 }
    )
    const countByShiftId = useMemo(() => {
        const raw = Array.isArray(operatorsData) ? operatorsData : (operatorsData?.warehouse_operators || operatorsData?.operators || [])
        const m = new Map()
        for (const op of raw) {
            if (op.operator_active === false) continue
            const sid = op.work_shift_id ?? op.work_shift?.id
            if (sid == null) continue
            m.set(sid, (m.get(sid) || 0) + 1)
        }
        return m
    }, [operatorsData])

    return (
        <>
            <PageView
                topMenu={
                    <PageTopBar>
                        <div className="text-lg font-semibold">{t('workforce.shifts.title')}</div>
                        <button
                            type="button"
                            onClick={() => setDialog({ shift: null })}
                            className="inline-flex items-center rounded-lg bg-yellow-500 px-3 py-2 text-sm font-medium text-white hover:bg-yellow-600"
                        >
                            + {t('workforce.shifts.create')}
                        </button>
                    </PageTopBar>
                }
                topMenuFullWidth={true}
                childrenFullWidth={true}
            >
                <WorkforceTabs />

                <div className="mt-4 rounded-md border border-blue-100 bg-blue-50">
                    <button
                        type="button"
                        onClick={() => setHelpOpen((v) => !v)}
                        className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm font-medium text-blue-900 hover:bg-blue-100/60 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-300"
                        aria-expanded={helpOpen}
                    >
                        <InformationCircleIcon className="h-4 w-4 flex-shrink-0 text-blue-500" />
                        <span className="flex-1">{t('workforce.shifts.help_title')}</span>
                        {helpOpen
                            ? <ChevronUpIcon className="h-4 w-4 text-blue-500" />
                            : <ChevronDownIcon className="h-4 w-4 text-blue-500" />}
                    </button>
                    {helpOpen && (
                        <div className="px-4 pb-4 pt-1 text-sm text-blue-900 space-y-3">
                            <div>
                                <div className="font-semibold">{t('workforce.shifts.help_create_title')}</div>
                                <ul className="list-disc pl-5 mt-1 space-y-1">
                                    <li>{t('workforce.shifts.help_create_1')}</li>
                                    <li>{t('workforce.shifts.help_create_2')}</li>
                                    <li>{t('workforce.shifts.help_create_3')}</li>
                                </ul>
                                <p className="mt-2 text-blue-800 italic">{t('workforce.shifts.help_create_note')}</p>
                            </div>
                            <div>
                                <div className="font-semibold">{t('workforce.shifts.help_edit_title')}</div>
                                <ul className="list-disc pl-5 mt-1 space-y-1">
                                    <li>{t('workforce.shifts.help_edit_1')}</li>
                                    <li>{t('workforce.shifts.help_edit_2')}</li>
                                    <li>{t('workforce.shifts.help_edit_3')}</li>
                                </ul>
                            </div>
                        </div>
                    )}
                </div>

                <div className="flex items-center gap-3 mt-4">
                    <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                        <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
                        {t('workforce.shifts.show_inactive')}
                    </label>
                </div>

                {isLoading ? (
                    <div className="mt-6"><Loader show={true} /></div>
                ) : isError ? (
                    <div className="mt-4 rounded bg-red-50 text-red-700 p-3 text-sm">{error?.message || t('workforce.errors.generic')}</div>
                ) : shifts.length === 0 ? (
                    <div className="mt-6 p-8 text-center text-gray-500 border rounded bg-white">
                        {t('workforce.shifts.empty')}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
                        {shifts.map((s) => (
                            <ShiftCard
                                key={s.id}
                                shift={s}
                                warehouse_id={warehouse_id}
                                operatorCount={countByShiftId.get(s.id) || 0}
                                onEdit={(shift) => setDialog({ shift })}
                            />
                        ))}
                    </div>
                )}
            </PageView>

            {dialog && (
                <ShiftFormDialog
                    shift={dialog.shift}
                    warehouse_id={warehouse_id}
                    onClose={() => setDialog(null)}
                />
            )}
        </>
    )
}
